import { KeyboardEvent, ReactNode, useEffect, useState } from "react";
import { unstable_batchedUpdates } from "react-dom";
import { useButtonThrottle } from "hooks/common";

import refreshIcon from "assets/svg/refresh-green.svg";
import { HoverAuthorizeTooltip } from "components/authorize/AuthorizeTooltip";
import ContentWrapper from "components/content-wrapper/ContentWrapper";
import {
    ACTUATOR_CONFIG_ACTIVATION_SUCCESS_MESSAGE,
    ACTUATOR_NAME_MESSAGE,
    ACTUATOR_NAME_SUCCESS_MESSAGE,
    HttpStatus,
    Patterns,
    TRY_AGAIN_ERROR_MESSAGE,
} from "constant";
import { useActuatorConfigurationContext } from "context/ActuatorConfigurationContext";
import moment from "moment";
import { Button, Col, Form } from "react-bootstrap";
import { useParams } from "react-router-dom";
import {
    getSensorLastReading,
    toggleActuatorActivation,
    updateActuatorCommand,
} from "service/gatewayService";
import { getAPIError, showErrorAlert, showSuccessAlert } from "utils/alert";
import { getCalendarFormat } from "utils/datetime";
import { formatValueWithUnit, isHttpSuccess } from "utils/functions";
import { formatReading, getActuatorState } from "utils/gatewayFunctions";
import { ACTIVATE, DEACTIVATE } from "constant/gateway";
import { Actuator, ActuatorCommandMode } from "types/Actuator";
import IconButton from "@mui/material/IconButton";

type ActuatorStatusProps = Readonly<{
    isRefreshing: boolean;
    renderStatus: () => ReactNode;
    refreshStatus: () => Promise<void>;
}>;

function ActuatorStatus({
    isRefreshing,
    renderStatus,
    refreshStatus,
}: ActuatorStatusProps): JSX.Element {
    return (
        <div className="actuator-summary mt-2">
            <div className="sensor-info">
                <h5 className="float-left">Status</h5>
            </div>

            <div className="last-active float-right text-right">
                <p>
                    {renderStatus()}
                    <span>
                        <IconButton onClick={refreshStatus}>
                            <img
                                className={
                                    isRefreshing
                                        ? "bigRefresh refresh-animation"
                                        : "bigRefresh"
                                }
                                src={refreshIcon}
                                alt="refresh-status"
                            />
                        </IconButton>
                    </span>
                </p>
            </div>
        </div>
    );
}

const ActuatorInfo = () => {
    const params: any = useParams();
    const { gatewayId, ldsuId, busNumber, said } = params;
    const { throttle, isThrottled } = useButtonThrottle(2000);
    const {
        actuator,
        actuator: { name, activate_ts, auto, mode },
        setActuator,
        setEditedActuator,
        gatewayName,
        ldsuName,
        type,
        editMode,
        isIOControllerState: { unit, isAnalog, appId, accuracy },
        isFourWired,
    } = useActuatorConfigurationContext();
    const [errorMsg, setErrorMsg] = useState("");
    const [editName, setEditName] = useState(name);
    const [editNameMode, setEditNameMode] = useState(false);

    const { gatewayNamePattern } = Patterns;

    const [statusState, setStatusState]: any = useState({
        status: "",
        timestamp: "",
    });

    const [isRefreshing, setIsRefreshing] = useState(false);

    const calendarFormat = getCalendarFormat();

    useEffect(() => {
        setEditName(name);
    }, [name]);

    useEffect(() => {
        const getStatus = async () => {
            const statusRes: any = await getSensorLastReading(
                params.gatewayId,
                params.ldsuId
            );
            if (statusRes.status === HttpStatus.OK) {
                const { value, ts } = statusRes.data;
                setStatusState({
                    status: value,
                    timestamp: ts,
                });
            }
        };
        getStatus();
    }, [params.gatewayId, params.ldsuId]);

    const renderStatus = () => {
        const { timestamp, status } = statusState;
        const { said } = params;

        const getStatus = () => {
            if (isAnalog)
                return (
                    <span className="reading">
                        {formatValueWithUnit(
                            formatReading(status[said], accuracy),
                            unit
                        )}
                    </span>
                );
            switch (status[said]) {
                case 1:
                    return (
                        <>
                            <i className="material-icons circle online">
                                circle
                            </i>
                            <span className="reading">Active</span>
                        </>
                    );
                case 0:
                    return (
                        <>
                            <i className="material-icons circle offline">
                                circle
                            </i>
                            <span className="reading">Inactive</span>
                        </>
                    );
                default:
                    return (
                        <span className="reading">
                            {status[said]} {unit}
                        </span>
                    );
            }
        };

        if (timestamp) {
            const timeString = moment(timestamp, "X").calendar(calendarFormat);
            return (
                <>
                    {getStatus()}
                    {` ${timeString}`}
                </>
            );
        } else {
            return <>Offline</>;
        }
    };

    const saveName = async () => {
        setErrorMsg("");
        const nameTrim = editName.trim();
        if (name === nameTrim) {
            setEditNameMode(false);
            setEditName(name);
            return;
        }

        if (!gatewayNamePattern.test(nameTrim)) {
            setErrorMsg(ACTUATOR_NAME_MESSAGE);
            return;
        }

        const configBody = {
            name: nameTrim,
        };

        const updateNameRes = await updateActuatorCommand(
            gatewayId,
            ldsuId,
            said,
            configBody
        );

        if (isHttpSuccess(updateNameRes.status)) {
            showSuccessAlert({
                message: ACTUATOR_NAME_SUCCESS_MESSAGE,
            });

            // batch the updates to prevent unnecessary re-renders
            // No longer needed in 2.5.0 because it use react 18
            unstable_batchedUpdates(() => {
                setActuator((prev: Actuator) => ({ ...prev, name: nameTrim }));
                setEditedActuator((prev: Actuator) => ({
                    ...prev,
                    name: nameTrim,
                }));
                setEditNameMode(false);
            });
        } else {
            showErrorAlert(getAPIError(updateNameRes, TRY_AGAIN_ERROR_MESSAGE));
        }
    };

    const refreshOneStatus = async () => {
        setIsRefreshing(true);
        const { data, status } = await getSensorLastReading(
            params.gatewayId,
            params.ldsuId
        );

        if (isHttpSuccess(status)) {
            setIsRefreshing(false);
            const { value, ts } = data;

            setStatusState({
                status: value,
                timestamp: ts,
            });
        }
        setIsRefreshing(false);
    };

    const renderLastActivated = () => {
        if (activate_ts) {
            const timeString = moment(activate_ts, "X").calendar(
                calendarFormat
            );
            return (
                <p>
                    Last activated
                    {` ${timeString[0].toLowerCase() + timeString.slice(1)}`}
                </p>
            );
        }
    };

    const renderLastControlled = () => {
        if (activate_ts) {
            const timeString = moment(activate_ts, "X").calendar(
                calendarFormat
            );
            return (
                <p>
                    Last controlled (manually)
                    {` ${timeString[0].toLowerCase() + timeString.slice(1)}`}
                </p>
            );
        }
    };

    const checkBeforeConfigure = (state: string, polarity?: boolean) => {
        if (editMode) {
            if (isFourWired) {
                if (state === "Activate") {
                    state = polarity ? "Forward" : "Reverse";
                } else if (state === "Deactivate") {
                    state = "Pause";
                }
            }
            showErrorAlert({
                message: (
                    <>
                        <span className="check-before-configure-msg">
                            Save configuration
                        </span>{" "}
                        before {state}
                    </>
                ),
            });
            return true;
        }
        return false;
    };

    const abort = async () => {
        if (checkBeforeConfigure("Abort")) return;

        const abortRes = await toggleActuatorActivation({
            gatewayId: params.gatewayId,
            deviceId: ldsuId,
            said,
            activate: false,
            immediately: true,
        });

        if (isHttpSuccess(abortRes.status)) {
            showSuccessAlert({
                message: ACTUATOR_CONFIG_ACTIVATION_SUCCESS_MESSAGE.fill({
                    state: "aborted",
                }),
            });
        } else {
            showErrorAlert(getAPIError(abortRes, TRY_AGAIN_ERROR_MESSAGE));
        }
    };

    const toggleActivation = async (
        activation_state: string,
        polarity?: boolean
    ) => {
        if (
            checkBeforeConfigure(
                activation_state === ACTIVATE ? "Activate" : "Deactivate",
                polarity
            )
        ) {
            return;
        }

        const payload: any = {
            gatewayId: params.gatewayId,
            deviceId: ldsuId,
            said,
            activate: activation_state === ACTIVATE,
        };

        if (polarity !== undefined) {
            payload.polarity = polarity;
        }

        const res = await toggleActuatorActivation(payload);

        if (isHttpSuccess(res.status)) {
            const { activate_ts } = res.data;

            if (activation_state === ACTIVATE) {
                setActuator({ ...actuator, activate_ts: activate_ts });
            }

            // calculate the new state based on the type and polarity
            const newState = getActuatorState({
                isFourWired,
                activation_state,
                polarity: polarity ?? false,
            });

            let successMessage;
            if (isFourWired) {
                if (activation_state === DEACTIVATE) {
                    successMessage = "Motor movement paused.";
                } else {
                    successMessage = `${newState} action activated successfully.`;
                }
            } else {
                successMessage =
                    ACTUATOR_CONFIG_ACTIVATION_SUCCESS_MESSAGE.fill({
                        state: newState.toLowerCase(),
                    });
            }

            showSuccessAlert({
                message: successMessage,
            });
        } else {
            showErrorAlert(getAPIError(res, TRY_AGAIN_ERROR_MESSAGE));
        }
    };

    const renderFourWireBtn = () => {
        return (
            <div className="d-flex w-100 flex-wrap">
                <HoverAuthorizeTooltip permission="gateway:update">
                    <Button
                        variant="primary"
                        className="btn-forward"
                        disabled={auto || isThrottled(`${said}-forward`)}
                        onClick={() => {
                            throttle(`${said}-forward`, () =>
                                toggleActivation(ACTIVATE, true)
                            );
                        }}
                    >
                        Forward
                    </Button>
                </HoverAuthorizeTooltip>
                <HoverAuthorizeTooltip permission="gateway:update">
                    <Button
                        variant="danger"
                        className="btn-reverse"
                        disabled={auto || isThrottled(`${said}-reverse`)}
                        onClick={() => {
                            throttle(`${said}-reverse`, () =>
                                toggleActivation(ACTIVATE, false)
                            );
                        }}
                    >
                        Reverse
                    </Button>
                </HoverAuthorizeTooltip>
                <HoverAuthorizeTooltip permission="gateway:update">
                    <Button
                        className="btn-pause"
                        disabled={auto || isThrottled(`${said}-pause`)}
                        onClick={() => {
                            throttle(`${said}-pause`, () =>
                                toggleActivation(DEACTIVATE)
                            );
                        }}
                    >
                        Pause
                    </Button>
                </HoverAuthorizeTooltip>
            </div>
        );
    };

    const renderActivateActionBtn = () => {
        return (
            <div className="d-flex w-100 flex-wrap">
                <HoverAuthorizeTooltip permission="gateway:update">
                    <Button
                        variant="primary"
                        className="btn-activate"
                        disabled={auto || isThrottled(`${said}-activate`)}
                        onClick={() => {
                            throttle(`${said}-activate`, () =>
                                toggleActivation(ACTIVATE)
                            );
                        }}
                    >
                        Activate
                    </Button>
                </HoverAuthorizeTooltip>
                <HoverAuthorizeTooltip permission="gateway:update">
                    <Button
                        variant="danger"
                        className="btn-deactivate"
                        disabled={auto || isThrottled(`${said}-deactivate`)}
                        onClick={() => {
                            throttle(`${said}-deactivate`, () =>
                                toggleActivation(DEACTIVATE)
                            );
                        }}
                    >
                        Deactivate
                    </Button>
                </HoverAuthorizeTooltip>
                <HoverAuthorizeTooltip permission="gateway:update">
                    <Button
                        className="btn-abort"
                        hidden={mode === ActuatorCommandMode.LEVEL || isAnalog}
                        disabled={auto}
                        onClick={abort}
                    >
                        Abort
                    </Button>
                </HoverAuthorizeTooltip>
            </div>
        );
    };

    return (
        <ContentWrapper>
            <Col lg={4} xs={12}>
                <div className="actuator-summary">
                    {isFourWired
                        ? renderFourWireBtn()
                        : renderActivateActionBtn()}

                    <div className="last-active mt-2">
                        {isFourWired
                            ? renderLastControlled()
                            : renderLastActivated()}
                    </div>

                    {auto &&
                        (!isFourWired ? (
                            <p className="mt-3">
                                This actuator can only be activated/deactivated
                                by Events as the 'Control' setting is set to
                                'Auto'
                            </p>
                        ) : (
                            <p className="mt-3">
                                This actuator can only be controlled by Events
                                as the 'Control' setting is set to 'Auto'
                            </p>
                        ))}
                </div>
                {!isFourWired && (
                    <ActuatorStatus
                        isRefreshing={isRefreshing}
                        renderStatus={renderStatus}
                        refreshStatus={refreshOneStatus}
                    />
                )}
                <div className="form-box mt-2">
                    <Form.Row>
                        <Col md="12">
                            <Form.Label>Actuator Name</Form.Label>
                        </Col>
                        <Col lg={8} xs={12} className="mb-2">
                            <Form.Group className="mb-0">
                                <Form.Control
                                    type="text"
                                    required
                                    aria-label="name"
                                    value={editName}
                                    disabled={!editNameMode}
                                    onChange={(e: any) => {
                                        setEditName(e.target.value);
                                    }}
                                    onKeyPress={(
                                        e: KeyboardEvent<HTMLInputElement>
                                    ) => {
                                        if (e.key === "Enter") {
                                            e.preventDefault();
                                            saveName();
                                        }
                                    }}
                                />
                            </Form.Group>
                        </Col>
                        <Col lg={4} xs={12}>
                            {editNameMode ? (
                                <Button
                                    type="button"
                                    className="w-100"
                                    onClick={saveName}
                                >
                                    Save
                                </Button>
                            ) : (
                                <HoverAuthorizeTooltip permission="gateway:update">
                                    <Button
                                        type="button"
                                        className="w-100"
                                        onClick={() => {
                                            setEditNameMode(true);
                                        }}
                                    >
                                        Edit
                                    </Button>
                                </HoverAuthorizeTooltip>
                            )}
                        </Col>
                        <Col>
                            <span className="text-danger" role="alert">
                                {errorMsg}
                            </span>
                        </Col>
                    </Form.Row>
                </div>
                <div className="form-box mt-2 ">
                    <Form.Row>
                        <Col md="12">
                            <div className="descriptor-item">
                                <h6 className="float-left">Type</h6>
                                <h6 className="ginfo float-right">{type}</h6>
                            </div>
                            {appId && (
                                <div className="descriptor-item">
                                    <h6 className="float-left">
                                        IO Controller App ID
                                    </h6>
                                    <h6 className="ginfo float-right">
                                        {appId}
                                    </h6>
                                </div>
                            )}

                            <div className="descriptor-item">
                                <h6 className="float-left">LDSU Name</h6>
                                <h6 className="ginfo float-right">
                                    {ldsuName}
                                </h6>
                            </div>
                            <div className="descriptor-item">
                                <h6 className="float-left">LDSU ID</h6>
                                <h6 className="ginfo float-right">{ldsuId}</h6>
                            </div>
                            <div className="descriptor-item">
                                <h6 className="float-left">Bus Port No.</h6>
                                <h6 className="ginfo float-right">
                                    {busNumber}
                                </h6>
                            </div>
                            <div className="descriptor-item">
                                <h6 className="float-left">Gateway Name</h6>
                                <h6 className="ginfo float-right">
                                    {gatewayName}
                                </h6>
                            </div>
                        </Col>
                    </Form.Row>
                </div>
            </Col>
        </ContentWrapper>
    );
};

export default ActuatorInfo;
